import React, { useState, useMemo } from 'react';

import PlannerIcons from '../../components/shared/PlannerIcons';
import { useToast } from '../../contexts/ToastContext';
import { GEMINI_API_KEY } from '../../config/firebase';

export default function ProjectTimeline({ project, updateProject }) {
  const [newEvent, setNewEvent] = useState({ date: project.date !== 'TBD' ? project.date : '', time: '', endTime: '', event: '', category: 'Main Event', assigned: '', location: '', notes: '' });
  
  const formatTime = (timeStr) => {
      if (!timeStr) return '';
      const [hours, minutes] = timeStr.split(':');
      const h = parseInt(hours, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const displayH = h % 12 || 12;
      return `${displayH}:${minutes} ${ampm}`;
  };

  // NEW: AI Timeline State
  const [showAiModal, setShowAiModal] = useState(false);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiConfig, setAiConfig] = useState({ type: 'Wedding', startTime: '17:00' });

  // NEW: Inline Edit State
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', time: '', endTime: '', event: '', category: 'Main Event', assigned: '', location: '', notes: '' });

  // NEW: Export Modal State
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilters, setExportFilters] = useState({ categories: [], assignees: [], vips: true, vendors: true, notes: true });

  // NEW: Logistics Conflict Detection State
  const [logisticsWarnings, setLogisticsWarnings] = useState({});
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  const toast = useToast();

  const categories = ['Setup', 'Vendor Arrival', 'Main Event', 'Dining', 'Photography', 'Teardown', 'Other'];
  
  const categoryColors = {
      'Setup': 'bg-blue-50 text-blue-700 border-blue-200',
      'Vendor Arrival': 'bg-purple-50 text-purple-700 border-purple-200',
      'Main Event': 'bg-pink-50 text-pink-700 border-pink-200',
      'Dining': 'bg-orange-50 text-orange-700 border-orange-200',
      'Photography': 'bg-teal-50 text-teal-700 border-teal-200',
      'Teardown': 'bg-gray-100 text-gray-700 border-gray-300',
      'Other': 'bg-slate-50 text-slate-700 border-slate-200'
  };

  const categoryDotColors = {
      'Setup': 'bg-blue-500 border-white',
      'Vendor Arrival': 'bg-purple-500 border-white',
      'Main Event': 'bg-pink-500 border-white',
      'Dining': 'bg-orange-500 border-white',
      'Photography': 'bg-teal-500 border-white',
      'Teardown': 'bg-gray-500 border-white',
      'Other': 'bg-slate-500 border-white'
  };

  const addEvent = (e) => {
    e.preventDefault();
    if (!newEvent.time || !newEvent.event || !newEvent.date) return;
    const tlEvent = { id: Date.now().toString(), ...newEvent };
    
    const currentTimeline = project.timeline || [];
    const newTimeline = [...currentTimeline, tlEvent].sort((a, b) => {
        // Sort by date, then time safely
        const dateA = a.date || '';
        const dateB = b.date || '';
        const dateCompare = dateA.localeCompare(dateB);
        if (dateCompare !== 0) return dateCompare;
        
        const timeA = a.time || '';
        const timeB = b.time || '';
        return timeA.localeCompare(timeB);
    });
    
    updateProject(project.id, { timeline: newTimeline });
    setNewEvent({ date: project.date !== 'TBD' ? project.date : '', time: '', endTime: '', event: '', category: 'Main Event', assigned: '', location: '', notes: '' });
  };

  const removeEvent = (id) => {
    updateProject(project.id, { timeline: project.timeline.filter(t => t.id !== id) });
    if (logisticsWarnings[id]) {
        const newWarnings = {...logisticsWarnings};
        delete newWarnings[id];
        setLogisticsWarnings(newWarnings);
    }
  };

  // NEW: Edit Handlers
  const startEdit = (item) => {
      setEditingId(item.id);
      setEditForm({ date: item.date || project.date || '', time: item.time, endTime: item.endTime || '', event: item.event, category: item.category || 'Other', assigned: item.assigned || '', location: item.location || '', notes: item.notes || '' });
  };

  const saveEdit = () => {
      if (!editForm.time || !editForm.event || !editForm.date) return;
      const newTimeline = (project.timeline || []).map(t => t.id === editingId ? { ...t, ...editForm } : t).sort((a, b) => {
          // Sort by date, then time safely
          const dateA = a.date || '';
          const dateB = b.date || '';
          const dateCompare = dateA.localeCompare(dateB);
          if (dateCompare !== 0) return dateCompare;
          
          const timeA = a.time || '';
          const timeB = b.time || '';
          return timeA.localeCompare(timeB);
      });
      updateProject(project.id, { timeline: newTimeline });
      setNewEvent({ date: project.date !== 'TBD' ? project.date : '', time: '', endTime: '', event: '', category: 'Main Event', assigned: '', location: '', notes: '' });
      setEditingId(null);
  };

  const handleOpenExportModal = () => {
      const assignees = [...new Set((project.timeline || []).map(t => t.assigned?.trim() || ''))];
      setExportFilters({
          categories: [...categories],
          assignees: assignees,
          vips: true,
          vendors: true,
          notes: true
      });
      setShowExportModal(true);
  };

  // NEW: Logistics Conflict Detection Logic
  const checkLogisticsConflicts = async () => {
      if (!window.google || !window.google.maps) {
          toast.error("Google Maps API is required to calculate travel times.");
          return;
      }

      if (!project.timeline || project.timeline.length < 2) {
          toast.error("Please add at least two events with locations to check for travel conflicts.");
          return;
      }

      setIsCheckingConflicts(true);
      setLogisticsWarnings({});
      const warnings = {};
      const service = new window.google.maps.DistanceMatrixService();

      const assignedEvents = {};
      project.timeline.forEach(item => {
          if (item.assigned && item.location && item.time) {
              if (!assignedEvents[item.assigned]) assignedEvents[item.assigned] = [];
              assignedEvents[item.assigned].push(item);
          }
      });

      for (const person of Object.keys(assignedEvents)) {
          const events = assignedEvents[person].sort((a, b) => a.time.localeCompare(b.time));
          
          for (let i = 0; i < events.length - 1; i++) {
              const ev1 = events[i];
              const ev2 = events[i+1];
              
              const [h1, m1] = ev1.time.split(':').map(Number);
              const [h2, m2] = ev2.time.split(':').map(Number);
              const timeDiffMins = (h2 * 60 + m2) - (h1 * 60 + m1);

              if (timeDiffMins > 0) {
                  try {
                      const response = await new Promise((resolve, reject) => {
                          service.getDistanceMatrix({
                              origins: [ev1.location],
                              destinations: [ev2.location],
                              travelMode: 'DRIVING',
                          }, (res, status) => {
                              if (status === 'OK') resolve(res);
                              else reject(status);
                          });
                      });

                      const element = response.rows[0].elements[0];
                      if (element.status === 'OK') {
                          const driveTimeMins = Math.ceil(element.duration.value / 60);
                          const buffer = 15; 
                          
                          if (driveTimeMins + buffer > timeDiffMins) {
                              warnings[ev2.id] = `🚨 Logistics Conflict! Driving from "${ev1.location}" takes ${driveTimeMins} mins. Only ${timeDiffMins} mins allotted before ${person}'s next event.`;
                          }
                      }
                  } catch (err) {
                      console.error("Distance Matrix Error:", err);
                  }
              }
          }
      }
      
      setLogisticsWarnings(warnings);
      setIsCheckingConflicts(false);
      
      if (Object.keys(warnings).length > 0) {
          toast.error(`Detected ${Object.keys(warnings).length} travel time conflict(s)!`);
      } else {
          toast.success("Timeline looks perfectly doable! No logistical conflicts detected.");
      }
  };

  // NEW: Generate AI Timeline
  const handleGenerateAiTimeline = async () => {
      if (!aiConfig.startTime) return;
      setIsAiGenerating(true);
      try {
          const prompt = `You are a professional event planner. Generate a standard, detailed day-of timeline for a ${aiConfig.type}. 
          The main event/start time is at ${aiConfig.startTime} (24-hour format). 
          Include standard logical steps: vendor arrivals, setup, guest arrivals, main events, and teardown. 
          Return strictly a JSON array of objects with exactly these keys: 
          - "time" (string in 24-hour "HH:MM" format)
          - "endTime" (string in 24-hour "HH:MM" format, optional estimate)
          - "event" (string, short description)
          - "category" (must be EXACTLY ONE of: "Setup", "Vendor Arrival", "Main Event", "Dining", "Photography", "Teardown", "Other")
          Example: [{"time": "14:00", "endTime": "15:30", "event": "Photographer arrives", "category": "Vendor Arrival"}]. Do not include markdown formatting.`;

          const payload = { 
              contents: [{ role: "user", parts: [{ text: prompt }] }], 
              generationConfig: { responseMimeType: "application/json" } 
          };
          
          const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`, { 
              method: 'POST', 
              headers: { 'Content-Type': 'application/json' }, 
              body: JSON.stringify(payload) 
          });
          
          const result = await response.json();
          if (result.candidates?.[0]) {
              const text = result.candidates[0].content.parts[0].text;
              const generatedItems = JSON.parse(text);
              
              const newTimelineItems = generatedItems.map((item, i) => ({
                  id: Date.now().toString() + i,
                  date: project.date !== 'TBD' ? project.date : new Date().toISOString().split('T')[0], // Default to main project date
                  time: item.time,
                  endTime: item.endTime || '',
                  event: item.event,
                  category: categories.includes(item.category) ? item.category : 'Other',
                  assigned: '',
                  notes: ''
              }));

              const currentTimeline = project.timeline || [];
              const mergedTimeline = [...currentTimeline, ...newTimelineItems].sort((a, b) => {
                  // Safely handle potentially missing dates/times from AI or legacy data
                  const dateA = a.date || '';
                  const dateB = b.date || '';
                  const dateCompare = dateA.localeCompare(dateB);
                  if (dateCompare !== 0) return dateCompare;
                  
                  const timeA = a.time || '';
                  const timeB = b.time || '';
                  return timeA.localeCompare(timeB);
              });
              
              updateProject(project.id, { timeline: mergedTimeline });
              setShowAiModal(false);
          } else {
              throw new Error("Invalid AI response");
          }
      } catch (err) {
          console.error("AI Timeline Generation Error:", err);
          alert("Failed to generate timeline. Please try again.");
      } finally {
          setIsAiGenerating(false);
      }
  };

  // Day-of Master Run of Show Exporter
  const handleExportRunOfShow = () => {
      const printWindow = window.open('', '_blank');
      
      const timelineHtml = (project.timeline || [])
          .filter(t => exportFilters.categories.includes(t.category || 'Other') && exportFilters.assignees.includes(t.assigned?.trim() || ''))
          .sort((a, b) => {
          // Safely handle potentially missing dates/times from legacy data
          const dateA = a.date || '';
          const dateB = b.date || '';
          const dateCompare = dateA.localeCompare(dateB);
          if (dateCompare !== 0) return dateCompare;
          
          const timeA = a.time || '';
          const timeB = b.time || '';
          return timeA.localeCompare(timeB);
      }).map(t => `
          <div style="margin-bottom: 15px; border-bottom: 1px solid #f0f0f0; padding-bottom: 10px; display: flex; gap: 20px;">
              <div style="min-width: 120px;">
                  <strong style="color: #6b7280; font-size: 12px; text-transform: uppercase;">${new Date(t.date).toLocaleDateString(undefined, {weekday: 'short', month: 'short', day: 'numeric'})}</strong><br/>
                  <strong style="color: #db2777; font-size: 15px;">${formatTime(t.time)}${t.endTime ? `<br/><span style="color: #6b7280; font-size: 13px; font-weight: normal;">to ${formatTime(t.endTime)}</span>` : ''}</strong>
              </div>
              <div>
                <span style="font-size: 16px; font-weight: bold; color: #111827;">${t.event}</span>
                <span style="font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; border: 1px solid #e5e7eb; padding: 2px 6px; border-radius: 12px; margin-left: 8px; display: inline-block; transform: translateY(-2px);">${t.category || 'Other'}</span>
                ${t.assigned ? `<br/><small style="color: #6b7280; font-weight: bold; background: #f3f4f6; padding: 2px 6px; border-radius: 4px; display: inline-block; margin-top: 4px;">👤 Assigned to: ${t.assigned}</small>` : ''}
                ${t.notes ? `<br/><span style="font-size: 13px; color: #4b5563; display: block; margin-top: 6px; border-left: 2px solid #d1d5db; padding-left: 8px;">${t.notes}</span>` : ''}
              </div>
          </div>
      `).join('');

      const vipsHtml = (project.vips || []).map(v => `
          <div style="margin-bottom: 10px; background: #f9fafb; padding: 10px; border-radius: 6px; border-left: 4px solid #db2777;">
              <strong style="display: block; font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 1px;">${v.role}</strong> 
              <span style="font-weight: bold; font-size: 16px;">${v.name}</span> <br/>
              <span style="color: #374151;">📞 ${v.phone || 'No phone listed'}</span>
          </div>
      `).join('');

      const vendorsHtml = (project.vendors || []).map(v => `
          <div style="margin-bottom: 10px; background: #f9fafb; padding: 10px; border-radius: 6px; border-left: 4px solid #4f46e5;">
              <strong style="display: block; font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 1px;">${v.category}</strong> 
              <span style="font-weight: bold; font-size: 16px;">${v.name}</span> <br/>
              <span style="color: #374151;">📞 ${v.phone || 'No phone listed'}</span>
          </div>
      `).join('');

      const notesHtml = (project.notes || []).map(n => `
          <div style="margin-bottom: 15px; background: #fffbeb; padding: 15px; border-radius: 6px; border-left: 4px solid #f59e0b; border: 1px solid #fef3c7;">
              <strong style="display: block; font-size: 16px; color: #111827; margin-bottom: 6px;">${n.title}</strong>
              <div style="color: #374151; font-size: 14px; white-space: pre-wrap; line-height: 1.5;">${n.content}</div>
          </div>
      `).join('');

      printWindow.document.write(`
          <html>
              <head>
                  <title>Run of Show - ${project.clientNames}</title>
                  <style>
                      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&display=swap');
                      body { font-family: 'Inter', sans-serif; padding: 40px; color: #1f2937; max-width: 900px; margin: 0 auto; -webkit-print-color-adjust: exact; }
                      .header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #111827; padding-bottom: 20px; margin-bottom: 30px; }
                      h1 { margin: 0; font-size: 32px; font-weight: 800; color: #111827; }
                      .meta { text-align: right; color: #6b7280; font-size: 14px; font-weight: 600; }
                      h2 { margin-top: 40px; margin-bottom: 20px; font-size: 20px; color: #111827; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; text-transform: uppercase; letter-spacing: 1px; }
                      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
                      @media print { .no-print { display: none; } body { padding: 0; } }
                  </style>
              </head>
              <body>
                  <div class="header">
                      <div>
                          <span style="background: #111827; color: white; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: bold; letter-spacing: 1px; margin-bottom: 10px; display: inline-block;">MASTER RUN-OF-SHOW</span>
                          <h1>${project.clientNames}</h1>
                      </div>
                      <div class="meta">
                          <p style="margin: 0;">Date: ${project.date !== 'TBD' ? new Date(project.date).toLocaleDateString() : 'TBD'}</p>
                          <p style="margin: 4px 0 0 0;">Location: ${project.location}</p>
                      </div>
                  </div>
                  
                  <div class="grid">
                      <div>
                          <h2>⭐ VIPs & Bridal Party</h2>
                          ${exportFilters.vips ? (vipsHtml || '<p style="color: #9ca3af; font-style: italic;">No VIP contacts added to project.</p>') : '<p style="color: #9ca3af; font-style: italic;">Excluded from this view.</p>'}
                      </div>
                      <div>
                          <h2>💼 Vendor Team</h2>
                          ${exportFilters.vendors ? (vendorsHtml || '<p style="color: #9ca3af; font-style: italic;">No vendor contacts added to project.</p>') : '<p style="color: #9ca3af; font-style: italic;">Excluded from this view.</p>'}
                      </div>
                  </div>

                  <h2>🕒 Event Timeline</h2>
                  ${timelineHtml || '<p style="color: #9ca3af; font-style: italic;">No timeline events scheduled for this view.</p>'}

                  ${(exportFilters.notes && project.notes && project.notes.length > 0) ? `
                      <h2>📝 Important Notes</h2>
                      ${notesHtml}
                  ` : ''}

                  <div class="no-print" style="margin-top: 50px; text-align: center;">
                      <button onclick="window.print()" style="padding: 12px 24px; background: #db2777; color: white; border: none; border-radius: 8px; font-weight: bold; cursor: pointer; box-shadow: 0 4px 6px rgba(219, 39, 119, 0.2);">Print / Save PDF</button>
                  </div>
              </body>
          </html>
      `);
      printWindow.document.close();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* NEW: AI Timeline Modal */}
      {showAiModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[200] flex items-center justify-center p-4">
            <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm border border-purple-100">
                <h3 className="text-xl font-bold mb-2 flex items-center gap-2 text-gray-900">
                    <span>✨</span> Auto-Draft Timeline
                </h3>
                <p className="text-sm text-gray-600 mb-6">Let AI generate an industry-standard run-of-show baseline for your event.</p>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Event Type</label>
                        <select value={aiConfig.type} onChange={e => setAiConfig({...aiConfig, type: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none">
                            <option>Wedding</option>
                            <option>Corporate Gala</option>
                            <option>Birthday Party</option>
                            <option>Baby Shower</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Main Start Time (e.g. Ceremony)</label>
                        <input type="time" value={aiConfig.startTime} onChange={e => setAiConfig({...aiConfig, startTime: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" required />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                    <button onClick={() => setShowAiModal(false)} className="px-5 py-2 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                    <button onClick={handleGenerateAiTimeline} disabled={isAiGenerating} className="px-5 py-2 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition shadow-md disabled:opacity-75 flex items-center gap-2">
                        {isAiGenerating ? <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> Thinking...</> : 'Generate Draft'}
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* UPDATED: Add Event Form with Notes and End Time */}
      <form onSubmit={addEvent} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-3 print:hidden">
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-end w-full">
            <div className="w-full md:w-40">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Date</label>
              <input type="date" value={newEvent.date} onChange={e => setNewEvent({ ...newEvent, date: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-md text-sm font-medium focus:border-pink-500 outline-none focus:ring-1 focus:ring-pink-500" required />
            </div>
            <div className="w-full md:w-32">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Start Time</label>
              <input type="time" value={newEvent.time} onChange={e => setNewEvent({ ...newEvent, time: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-md text-sm font-medium focus:border-pink-500 outline-none focus:ring-1 focus:ring-pink-500" required />
            </div>
            <div className="w-full md:w-32">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">End Time (opt)</label>
              <input type="time" value={newEvent.endTime} onChange={e => setNewEvent({ ...newEvent, endTime: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-md text-sm font-medium focus:border-pink-500 outline-none focus:ring-1 focus:ring-pink-500" />
            </div>
            <div className="w-full md:w-40">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Category</label>
              <select value={newEvent.category} onChange={e => setNewEvent({ ...newEvent, category: e.target.value })} className="w-full p-2.5 border border-gray-300 rounded-md text-sm font-medium focus:border-pink-500 outline-none focus:ring-1 focus:ring-pink-500 bg-white">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="w-full md:flex-1">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Event Description</label>
              <input type="text" value={newEvent.event} onChange={e => setNewEvent({ ...newEvent, event: e.target.value })} placeholder="e.g. Photographer Arrives" className="w-full p-2.5 border border-gray-300 rounded-md text-sm font-medium focus:border-pink-500 outline-none focus:ring-1 focus:ring-pink-500" required />
            </div>
        </div>
        <div className="flex flex-col md:flex-row gap-3 items-end w-full">
            <div className="w-full md:w-1/4">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Assigned To (opt)</label>
              <input type="text" value={newEvent.assigned} onChange={e => setNewEvent({ ...newEvent, assigned: e.target.value })} placeholder="e.g. Bridal Party" className="w-full p-2.5 border border-gray-300 rounded-md text-sm font-medium focus:border-pink-500 outline-none focus:ring-1 focus:ring-pink-500" />
            </div>
            <div className="w-full md:w-1/4">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Location</label>
              <input type="text" value={newEvent.location || ''} onChange={e => setNewEvent({ ...newEvent, location: e.target.value })} placeholder="e.g. Main Hall" className="w-full p-2.5 border border-gray-300 rounded-md text-sm font-medium focus:border-pink-500 outline-none focus:ring-1 focus:ring-pink-500" />
            </div>
            <div className="w-full md:flex-1">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Detailed Notes (opt)</label>
              <input type="text" value={newEvent.notes || ''} onChange={e => setNewEvent({ ...newEvent, notes: e.target.value })} placeholder="e.g. Ensure all flowers are delivered to the bridal suite" className="w-full p-2.5 border border-gray-300 rounded-md text-sm font-medium focus:border-pink-500 outline-none focus:ring-1 focus:ring-pink-500" />
            </div>
            <button type="submit" className="w-full md:w-auto bg-gray-900 text-white px-8 py-2.5 rounded-md text-sm font-bold hover:bg-black transition shadow-sm h-[42px] whitespace-nowrap">Add Event</button>
        </div>
      </form>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 md:p-6 print:shadow-none print:border-none print:p-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 md:mb-8 border-b border-gray-100 pb-4 sm:border-0 sm:pb-0 print:border-b print:pb-4">
            <div className="flex items-center gap-3">
              <h3 className="font-serif text-xl md:text-2xl font-bold text-gray-900">Day-of Itinerary</h3>
              <button 
                  onClick={checkLogisticsConflicts} 
                  disabled={isCheckingConflicts}
                  className="print:hidden bg-amber-100 text-amber-800 hover:bg-amber-200 px-3 md:px-4 py-2 text-xs font-bold rounded-lg transition-colors flex items-center gap-2 shadow-sm border border-amber-200 whitespace-nowrap"
              >
                  {isCheckingConflicts ? <span className="animate-spin">🔄</span> : '🚗'} <span className="hidden sm:inline">Check Logistics</span>
              </button>
              <button onClick={() => setShowAiModal(true)} className="print:hidden bg-purple-50 text-purple-600 hover:bg-purple-100 px-3 md:px-4 py-2 text-xs font-bold rounded-lg transition-colors flex items-center gap-2 shadow-sm border border-purple-100 whitespace-nowrap">
                <span>✨</span> <span className="hidden sm:inline">AI Draft</span>
              </button>
              {/* Export Run of Show Button */}
              <button onClick={handleOpenExportModal} className="print:hidden bg-pink-50 text-pink-600 hover:bg-pink-100 px-3 md:px-4 py-2 text-xs font-bold rounded-lg transition-colors flex items-center gap-2 shadow-sm border border-pink-100 whitespace-nowrap">
                <PlannerIcons.Document /> <span className="hidden sm:inline">Print Run-of-Show</span>
              </button>
            </div>
            <span className="text-xs md:text-sm font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full self-start sm:self-auto print:bg-transparent print:px-0 print:text-gray-800">{new Date(project.date).toLocaleDateString()}</span>
        </div>
        
        <div className="relative border-l-2 border-gray-200 ml-2 md:ml-3 space-y-6 md:space-y-8">
          {(project.timeline || []).map((item) => (
            <div key={item.id} className="relative pl-5 md:pl-6 group animate-fade-in">
              <div className={`absolute w-3 h-3 rounded-full -left-[7px] top-2 border-2 shadow-sm ${categoryDotColors[item.category || 'Other'] || categoryDotColors['Other']}`} />
              
              {editingId === item.id ? (
                  // Inline Edit UI
                  <div className="bg-white p-4 rounded-xl border-2 border-pink-300 shadow-md z-10 relative flex flex-col gap-3">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                          <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Date</label>
                              <input type="date" value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-pink-500 outline-none font-bold" required />
                          </div>
                          <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Start Time</label>
                              <input type="time" value={editForm.time} onChange={e => setEditForm({...editForm, time: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-pink-500 outline-none font-bold" />
                          </div>
                          <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">End Time</label>
                              <input type="time" value={editForm.endTime} onChange={e => setEditForm({...editForm, endTime: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-pink-500 outline-none font-bold" />
                          </div>
                          <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Category</label>
                              <select value={editForm.category} onChange={e => setEditForm({...editForm, category: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-pink-500 outline-none font-bold bg-white">
                                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                          </div>
                          <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Assigned</label>
                              <input type="text" value={editForm.assigned} onChange={e => setEditForm({...editForm, assigned: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-pink-500 outline-none" placeholder="Assigned to" />
                          </div>
                          <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Location</label>
                              <input type="text" value={editForm.location || ''} onChange={e => setEditForm({...editForm, location: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-pink-500 outline-none" placeholder="Location" />
                          </div>
                          <div className="md:col-span-5">
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Event Description</label>
                              <input type="text" value={editForm.event} onChange={e => setEditForm({...editForm, event: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-pink-500 outline-none font-bold" placeholder="Event description" />
                          </div>
                      </div>
                      <input type="text" value={editForm.notes || ''} onChange={e => setEditForm({...editForm, notes: e.target.value})} className="p-2 border border-gray-300 rounded-md text-sm focus:ring-pink-500 outline-none w-full mt-1" placeholder="Detailed notes (optional)..." />
                      <div className="flex gap-2 justify-end mt-1">
                          <button onClick={() => setEditingId(null)} className="px-5 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-bold transition">Cancel</button>
                          <button onClick={saveEdit} className="px-5 py-2 bg-pink-600 text-white hover:bg-pink-700 rounded-lg text-sm font-bold transition shadow-sm">Save Changes</button>
                      </div>
                  </div>
              ) : (
                  // Normal View UI
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start bg-gray-50/50 p-4 rounded-xl border border-transparent hover:border-gray-200 hover:bg-white transition-all shadow-sm gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className={`font-bold text-xs md:text-sm tracking-wide px-2.5 py-1 rounded-md border ${categoryColors[item.category || 'Other'] || categoryColors['Other']}`}>
                              {item.date && item.date !== project.date && <span className="text-gray-600 font-medium mr-2">{new Date(item.date).toLocaleDateString(undefined, {weekday: 'short', month: 'short', day: 'numeric'})}</span>}
                              {formatTime(item.time)} {item.endTime && <span className="text-gray-500 font-medium ml-1">to {formatTime(item.endTime)}</span>}
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{item.category || 'Other'}</span>
                      </div>
                      <h4 className="font-bold text-gray-900 text-base md:text-lg leading-tight mt-1 mb-1">{item.event}</h4>
                      {item.notes && <p className="text-sm text-gray-600 mb-2 leading-relaxed border-l-2 border-gray-200 pl-3 mt-2 italic">{item.notes}</p>}
                      <div className="flex flex-wrap gap-3 mt-2">
                          {item.assigned && <p className="text-xs md:text-sm text-gray-500 flex items-center gap-1.5 font-medium bg-gray-100 w-fit px-2 py-1 rounded"><PlannerIcons.Users /> <span>{item.assigned}</span></p>}
                          {item.location && <p className="text-xs md:text-sm text-blue-600 flex items-center gap-1.5 font-medium bg-blue-50 w-fit px-2 py-1 rounded"><span>📍</span> <span>{item.location}</span></p>}
                      </div>
                      {logisticsWarnings[item.id] && (
                          <div className="mt-3 text-sm font-bold text-red-700 bg-red-100/50 border border-red-200 p-3 rounded-lg flex items-start gap-2 shadow-sm">
                              <span>{logisticsWarnings[item.id]}</span>
                          </div>
                      )}
                    </div>
                    <div className="flex self-end sm:self-start gap-1.5 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity mt-2 sm:mt-0">
                      <button onClick={() => startEdit(item)} className="p-2 text-blue-500 hover:text-blue-700 transition-colors bg-blue-50 hover:bg-blue-100 rounded-full print:hidden shadow-sm" title="Edit Event">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                      </button>
                      <button onClick={() => removeEvent(item.id)} className="p-2 text-red-500 hover:text-red-700 transition-colors bg-red-50 hover:bg-red-100 rounded-full print:hidden shadow-sm" title="Delete Event">
                        <PlannerIcons.Trash />
                      </button>
                    </div>
                  </div>
              )}
            </div>
          ))}
          {(!project.timeline || project.timeline.length === 0) && (
            <div className="pl-6 text-gray-400 italic text-sm font-medium">No events scheduled yet.</div>
          )}
        </div>
      </div>

      {/* NEW: Export Modal */}
      {showExportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-[200] flex items-center justify-center p-4 animate-fade-in">
              <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-lg border border-pink-100 max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3">
                      <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                          <PlannerIcons.Document /> Custom PDF Export
                      </h3>
                      <button onClick={() => setShowExportModal(false)} className="text-gray-400 hover:text-gray-800 text-xl">&times;</button>
                  </div>
                  
                  <div className="space-y-6">
                      <div>
                          <h4 className="text-sm font-bold text-gray-700 mb-2">Include Categories</h4>
                          <div className="grid grid-cols-2 gap-2">
                              {categories.map(c => (
                                  <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                                      <input 
                                          type="checkbox" 
                                          checked={exportFilters.categories.includes(c)} 
                                          onChange={(e) => {
                                              const newCats = e.target.checked 
                                                  ? [...exportFilters.categories, c] 
                                                  : exportFilters.categories.filter(x => x !== c);
                                              setExportFilters({...exportFilters, categories: newCats});
                                          }}
                                          className="text-pink-600 focus:ring-pink-500 rounded" 
                                      />
                                      {c}
                                  </label>
                              ))}
                          </div>
                      </div>

                      <div>
                          <h4 className="text-sm font-bold text-gray-700 mb-2">Include Roles / Assignees</h4>
                          <div className="grid grid-cols-2 gap-2">
                              {[...new Set((project.timeline || []).map(t => t.assigned?.trim() || ''))].map(a => (
                                  <label key={a} className="flex items-center gap-2 text-sm cursor-pointer">
                                      <input 
                                          type="checkbox" 
                                          checked={exportFilters.assignees.includes(a)} 
                                          onChange={(e) => {
                                              const newAssignees = e.target.checked 
                                                  ? [...exportFilters.assignees, a] 
                                                  : exportFilters.assignees.filter(x => x !== a);
                                              setExportFilters({...exportFilters, assignees: newAssignees});
                                          }}
                                          className="text-pink-600 focus:ring-pink-500 rounded" 
                                      />
                                      {a || 'Unassigned'}
                                  </label>
                              ))}
                          </div>
                          {[...new Set((project.timeline || []).map(t => t.assigned?.trim() || ''))].length === 0 && (
                              <p className="text-xs text-gray-500 italic">No roles assigned in your timeline.</p>
                          )}
                      </div>

                      <div className="border-t border-gray-100 pt-4">
                          <h4 className="text-sm font-bold text-gray-700 mb-2">Additional Sections</h4>
                          <div className="space-y-2">
                              <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <input type="checkbox" checked={exportFilters.vips} onChange={e => setExportFilters({...exportFilters, vips: e.target.checked})} className="text-pink-600 focus:ring-pink-500 rounded" />
                                  VIPs & Bridal Party
                              </label>
                              <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <input type="checkbox" checked={exportFilters.vendors} onChange={e => setExportFilters({...exportFilters, vendors: e.target.checked})} className="text-pink-600 focus:ring-pink-500 rounded" />
                                  Vendor Team
                              </label>
                              <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <input type="checkbox" checked={exportFilters.notes} onChange={e => setExportFilters({...exportFilters, notes: e.target.checked})} className="text-pink-600 focus:ring-pink-500 rounded" />
                                  Important Notes
                              </label>
                          </div>
                      </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                      <button onClick={() => setShowExportModal(false)} className="px-5 py-2 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                      <button onClick={() => { handleExportRunOfShow(); setShowExportModal(false); }} className="px-5 py-2 bg-pink-600 text-white rounded-lg font-bold hover:bg-pink-700 transition shadow-md flex items-center gap-2">
                          <PlannerIcons.Printer /> Generate PDF
                      </button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
}
